import { Product } from '@/queries/products/types';
import { useSelector } from '@/redux/helper';

/**
 * Whether the API has told us this visitor cannot buy this product.
 *
 * Eligibility is a judgement about a *user* — "you already hold this", "no habitat selected" — so
 * there is nothing to judge for someone who is not signed in, and the API cannot answer for them.
 * Treating that unanswered case as a refusal would disable every product for guests, which is
 * exactly the audience the Guest Purchase Flow exists for: they pick first and sign up after.
 *
 * So a guest is never blocked here. They still cannot buy anything without an account — the flow
 * routes them through sign-up, and the server re-checks eligibility when the session is created.
 *
 * Note this answers "has the API refused", not "is the card ready". A product that has not loaded
 * yet is neither; callers disable on `!product` separately. Conflating the two is what used to keep
 * the button disabled until the product arrived, and dropping that check here made it clickable
 * too early — a click before the product existed silently did nothing.
 */
const useEligibilityBlocked = (product: Product | null | undefined) => {
  const logged = useSelector((state) => state.user.logged);
  return !!logged && !product?.eligibility?.allowed;
};

export default useEligibilityBlocked;
